import {
	AddDeliveryNotePaymentsRequest,
	AddDeliveryNotePaymentsResponse,
	BuyerCustomerDTO,
	CancelDeliveryNotesFromPosRequest,
	CanReopenDeliveryNoteRequest,
	CanReopenDeliveryNoteResponse,
	CanReopenDeliveryNoteResultDTO,
	ChangeDeliveryNoteCustomerRequest,
	CreateDeliveryNoteFromPosRequest,
	CreateDeliveryNoteFromPosResponse,
	CreateDeliveryNoteFromSalesOrderFromPosRequest,
	CreateDeliveryNoteFromSalesOrderFromPosResponse,
	CreateDeliveryNoteFromSalesOrderParamsFromPosDTO,
	CreateDeliveryNotesInvoiceFromPosRequest,
	CreateDeliveryNotesInvoiceFromPosResponse,
	CreateDeliveryNotesRefundFromPosRequest,
	CreateDeliveryNotesRefundFromPosResponse,
	DeliveryNoteDraftDTO,
	DeliveryNoteDTO,
	DeliveryNoteParamsFromPosDTO,
	DocumentEmailOptionsDTO,
	GetAmountToPayFromDeliveryNotesRequest,
	GetAmountToPayFromDeliveryNotesResponse,
	GetDeliveryNoteDraftRequest,
	GetDeliveryNoteDraftResponse,
	GetDeliveryNoteEmailOptionsRequest,
	GetDeliveryNoteEmailOptionsResponse,
	GetPendingDeliveryNoteByIdRequest,
	GetPendingDeliveryNoteByIdResponse,
	GetPendingDeliveryNotesByTextRequest,
	GetPendingDeliveryNotesByTextResponse,
	GetQuantityByProductLineRequest,
	GetQuantityByProductLineResponse,
	GetTicketByIdRequest,
	GetTicketByIdResponse,
	InvoiceParamsFromPosDTO,
	PaymentDTO,
	QuantityLineDTO,
	ReopenDeliveryNoteFromPosRequest,
	ReopenDeliveryNoteFromPosResponse,
	SaleDTO,
	InternalOrderServeMode,
	SendDeliveryNoteEmailRequest,
	SendDeliveryNoteEmailResponse,
	SendDocumentEmailResultDTO,
	SetDeliveryNoteTableCompanionsRequest,
	TicketDTO,
	TicketLocationDTO
} from 'core/api';
import { Bus, SessionProvider } from 'core/infrastructure';

const process = Bus.buildProcessFn('/bus/');

export async function searchByText(text: string): Promise<DeliveryNoteDTO[]> {
	const request = new GetPendingDeliveryNotesByTextRequest({ text });
	return process(request, (response: GetPendingDeliveryNotesByTextResponse) => response.DeliveryNotes);
}

export function getTicket(note: DeliveryNoteDTO): Promise<TicketDTO> {
	const request = new GetTicketByIdRequest({ ticketId: note.TicketId, claimTicket: false });
	return process(request, (response: GetTicketByIdResponse) => response.Ticket);
}

export async function addPayments(deliveryNoteId: number, payments: PaymentDTO[], prepayAmount: number): Promise<SaleDTO> {
	const request = new AddDeliveryNotePaymentsRequest({ deliveryNoteId, payments, prepayAmount });
	return process(request, (response: AddDeliveryNotePaymentsResponse) => response.Sale);
}

export async function canBeReopened(deliveryNoteId: number): Promise<CanReopenDeliveryNoteResultDTO> {
	const request = new CanReopenDeliveryNoteRequest({ deliveryNoteId });
	return process(request, (response: CanReopenDeliveryNoteResponse) => response.Result);
}

export async function reopen(deliveryNoteId: number, location: TicketLocationDTO): Promise<TicketDTO> {
	const request = new ReopenDeliveryNoteFromPosRequest({ deliveryNoteId, params: { Location: location, TargetTicketId: null } });
	return process(request, (response: ReopenDeliveryNoteFromPosResponse) => response.Ticket);
}

export async function reopenInTicket(deliveryNoteId: number, location: TicketLocationDTO, targetTicketId: number): Promise<TicketDTO> {
	const request = new ReopenDeliveryNoteFromPosRequest({ deliveryNoteId, params: { Location: location, TargetTicketId: targetTicketId } });
	return process(request, (response: ReopenDeliveryNoteFromPosResponse) => response.Ticket);
}

export async function changeCustomer(deliveryNoteId: number, customer: BuyerCustomerDTO): Promise<void> {
	const request = new ChangeDeliveryNoteCustomerRequest({ deliveryNoteId, customer });
	await process(request);
}

export async function getEmailOptions(deliveryNoteId: number): Promise<DocumentEmailOptionsDTO> {
	const request = new GetDeliveryNoteEmailOptionsRequest({ deliveryNoteId });
	return await process(request, (response: GetDeliveryNoteEmailOptionsResponse) => response.Options);
}

export async function sendEmail(options: DocumentEmailOptionsDTO): Promise<SendDocumentEmailResultDTO> {
	const request = new SendDeliveryNoteEmailRequest({ options });
	return await process(request, (response: SendDeliveryNoteEmailResponse) => response.Result);
}

export async function createRefund(deliveryNoteIds: number[], params: InvoiceParamsFromPosDTO): Promise<SaleDTO> {
	const request = new CreateDeliveryNotesRefundFromPosRequest({ deliveryNoteIds, params });
	return process(request, (response: CreateDeliveryNotesRefundFromPosResponse) => response.Sale);
}

export async function createInvoice(deliveryNoteIds: number[], params: InvoiceParamsFromPosDTO): Promise<SaleDTO> {
	const request = new CreateDeliveryNotesInvoiceFromPosRequest({ deliveryNoteIds, params });
	return process(request, (response: CreateDeliveryNotesInvoiceFromPosResponse) => response.Sale);
}

export async function cancel(deliveryNoteIds: number[], printCancellations: boolean): Promise<void> {
	const request = new CancelDeliveryNotesFromPosRequest({ deliveryNoteIds, shouldPrint: printCancellations });
	await process(request);
}

export function create(ticketId: number, params: DeliveryNoteParamsFromPosDTO): Promise<SaleDTO> {
	const request = new CreateDeliveryNoteFromPosRequest({ ticketId, params });
	return process(request, (response: CreateDeliveryNoteFromPosResponse) => response.Sale);
}



export async function getDraftByTicketId(ticketId: number): Promise<DeliveryNoteDraftDTO> {
	const request = new GetDeliveryNoteDraftRequest({ ticketId });
	return process(request, (response: GetDeliveryNoteDraftResponse) => response.Draft);
}

export async function getById(deliveryNoteId: number): Promise<DeliveryNoteDTO> {
	const request = new GetPendingDeliveryNoteByIdRequest({ deliveryNoteId, isTrainingMode: SessionProvider.getCurrent().IsTrainingMode });
	return process(request, (response: GetPendingDeliveryNoteByIdResponse) => response.DeliveryNote);
}

export function getPendingAmount(deliveryNoteIds: number[]): Promise<number> {
	const request = new GetAmountToPayFromDeliveryNotesRequest({ deliveryNoteIds });
	return process(request, (response: GetAmountToPayFromDeliveryNotesResponse) => response.Amount);
}


export function setTableCompanions(deliveryNoteId: number, tableCompanions: Nullable<number>) {
	const request = new SetDeliveryNoteTableCompanionsRequest({ deliveryNoteId, tableCompanions });
	return process(request);
}

export function getQuantityByProductLine(orderIds: number[]): Promise<QuantityLineDTO[]> {
	const request = new GetQuantityByProductLineRequest({ orderIds });
	return process(request, (response: GetQuantityByProductLineResponse) => response.QuantityLines);
}

export async function convertToDeliveryNote(salesOrderIds: number[]): Promise<SaleDTO> {
	const params: CreateDeliveryNoteFromSalesOrderParamsFromPosDTO = {
		SalesOrderIds: salesOrderIds,
		InternalOrderServeMode: InternalOrderServeMode.CancelPending,
		QuantityByProductLine: await getQuantityByProductLine(salesOrderIds)
	};
	const request = new CreateDeliveryNoteFromSalesOrderFromPosRequest({ params });
	return process(request, (response: CreateDeliveryNoteFromSalesOrderFromPosResponse) => response.Sale);
}
